import { useCallback, useEffect, useState } from 'react';

import { Button } from '@bambi/component-library/src/components/Button/Button';
import { DefaultFormFieldLayout } from '@bambi/component-library/src/components/forms/DefaultFormFieldLayout/DefaultFormFieldLayout';
import { Select } from '@bambi/component-library/src/components/inputs/Select/Select';
import { SimpleTooltip } from '@bambi/component-library/src/components/Tooltip/SimpleTooltip';
import { InformationCircleIcon } from '@heroicons/react/20/solid';
import { useDispatch, useSelector } from 'react-redux';

import type { RootState } from 'src/app/store/types';
import { usePayersandtemplatesRetrieveQuery } from 'src/common/external/bambi-api/bambiApi';
import { FormErrorMessage } from 'src/common/FormErrorMessage';
import { Modal } from 'src/common/primitives/Modal';
import {
  selectUploadPayerId,
  selectUploadPayerTemplate,
  setShowUploadDetailsModal,
  setShowUploadModal,
} from 'src/features/trip/trip.slice';

import { UploadDetailsSampleDownloadFile } from './UploadDetailsSample';

import type { SelectOption } from '@bambi/component-library/src/components/inputs/Select/SelectOption/SelectOption';

export function UploadDetailsModal() {
  const dispatch = useDispatch();

  const open = useSelector(
    (state: RootState) => state.trip.showUploadDetailsModal
  );

  const [payer, setPayer] = useState<SelectOption | undefined>(undefined);
  const [payerOptions, setPayerOptions] = useState<SelectOption[] | undefined>(
    undefined
  );
  const [payerTemplate, setPayerTemplate] = useState<SelectOption | undefined>(
    undefined
  );
  const [payerTemplateOptions, setPayerTemplateOptions] = useState<
    SelectOption[] | undefined
  >(undefined);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  const { data: payers } = usePayersandtemplatesRetrieveQuery(
    {},
    { skip: !open }
  );

  const closeModal = useCallback(() => {
    setError(undefined);
    setLoading(false);
    dispatch(setShowUploadDetailsModal(false));
    setPayer(undefined);
    setPayerTemplate(undefined);
  }, [dispatch]);

  const handleSubmit = () => {
    setError(undefined);
    setLoading(true);
    if (payerTemplate && payer) {
      dispatch(setShowUploadModal(true));
      dispatch(selectUploadPayerTemplate(payerTemplate));
      dispatch(selectUploadPayerId(payer));
    }
  };

  const onPayerChange = (selected: SelectOption | undefined) => {
    if (selected) {
      setPayer(selected);
      if (payer?.value === 'multiple') {
        setPayerTemplate(undefined);
      } else {
        const newPayer = payers?.payer_templates.find(
          (x) => x.id === payer?.value
        );
        if (newPayer?.default_template) {
          setPayerTemplate({
            label: newPayer.default_template.template_name,
            value: newPayer.default_template.template_key,
          });
        } else {
          setPayerTemplate(undefined);
        }
      }
    }
  };

  useEffect(() => {
    if (!open) {
      setError(undefined);
      setLoading(false);
      setPayer(undefined);
      setPayerTemplate(undefined);
    }
  }, [open]);

  useEffect(() => {
    if (payers) {
      const singlePayerOptions = payers.payer_templates
        .filter((payer) => payer.active_templates.length > 0)
        .map((payer) => {
          return {
            label: payer.payer_display_name,
            value: payer.id,
          };
        });

      const payerOptions = payers.multi_payer_templates.length
        ? [
            ...singlePayerOptions,
            { label: '', value: 'select-separator' },
            { label: 'Multiple Payers', value: '' },
          ]
        : singlePayerOptions;

      setPayerOptions(payerOptions);
    }
  }, [payers]);

  useEffect(() => {
    if (payer) {
      if (payer.value === '') {
        const multiPayerTemplates = payers?.multi_payer_templates.map(
          (template) => {
            return {
              label: template.template_name,
              value: template.template_key,
            };
          }
        );

        setPayerTemplateOptions(multiPayerTemplates);
        if (multiPayerTemplates?.length === 1) {
          setPayerTemplate(multiPayerTemplates[0]);
        }
      } else {
        const payerActiveTemplates = payers?.payer_templates.find(
          (x) => x.id === payer.value
        )?.active_templates;

        const payerTemplates = payerActiveTemplates?.map((template) => {
          return {
            label: template.template_name,
            value: template.template_key,
          };
        });

        setPayerTemplateOptions(payerTemplates);

        if (payerTemplates?.length === 1) {
          setPayerTemplate(payerTemplates[0]);
        }
      }
    }
  }, [payer, payers?.multi_payer_templates, payers?.payer_templates]);

  return (
    <Modal
      open={open}
      setOpen={closeModal}
      contentClassnames="max-w-6xl"
      title="Select a CSV template"
    >
      <h1 className="text-2xl">Select a CSV template</h1>
      <p>You'll upload and verify the data in the next step</p>
      {payer?.label === 'Multiple Payers' ? (
        <UploadDetailsSampleDownloadFile />
      ) : null}
      {error && <FormErrorMessage>{error}</FormErrorMessage>}
      {payerOptions?.length === 0 && (
        <FormErrorMessage>None of your payers have templates.</FormErrorMessage>
      )}
      <div className="flex flex-col gap-3 py-4">
        <div>
          <DefaultFormFieldLayout label="Payer" inputId="payer">
            <Select
              placeholder="Select payer"
              selected={payer}
              options={payerOptions || []}
              onChange={(selected) => {
                onPayerChange(selected);
              }}
            ></Select>
          </DefaultFormFieldLayout>
        </div>
        <div>
          <DefaultFormFieldLayout
            // this is hard coded for now, but we can add this to the payer
            // or template model if we get more than 1 special snowflake
            label={
              <div className="flex items-center gap-1">
                <span>Template</span>
                {payer?.label === 'Access 2 Care' && (
                  <SimpleTooltip
                    trigger={
                      <button
                        type="button"
                        className="text-gray-500 hover:text-gray-700"
                        aria-label="Access 2 Care template info"
                      >
                        <InformationCircleIcon className="h-4 w-4" />
                      </button>
                    }
                    content={
                      <div className="max-w-xs text-sm">
                        <p className="mb-2">
                          Access 2 Care can have many different address fields.
                          You’ll either have to map these in the next step, or
                          edit the upload file to use:
                        </p>
                        <ul className="list-inside list-disc space-y-1">
                          <li>Pickup Address (street, unit)</li>
                          <li>Pickup Address (city, state, zip)</li>
                        </ul>
                        <p>Or:</p>
                        <ul className="list-inside list-disc space-y-1">
                          <li>Pickup Address (full)</li>
                        </ul>
                        <br></br>
                        <p>Optionally, you can include:</p>
                        <ul className="list-inside list-disc space-y-1">
                          <li>Pickup Address (unit)</li>
                        </ul>
                      </div>
                    }
                  />
                )}
              </div>
            }
            inputId="template"
          >
            <Select
              placeholder="Select template"
              selected={payerTemplate}
              options={payerTemplateOptions || []}
              onChange={(selected) => {
                if (selected) setPayerTemplate(selected);
              }}
              disabled={!payer}
            ></Select>
          </DefaultFormFieldLayout>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4 pt-6">
        <Button disabled={loading} onClick={() => closeModal()}>
          Cancel
        </Button>
        <Button
          disabled={loading || !payer || !payerTemplate}
          variant="primary"
          onClick={() => handleSubmit()}
          dataTestId="upload-details-modal-upload-button"
        >
          {loading ? 'Uploading' : 'Upload'}
        </Button>
      </div>
    </Modal>
  );
}
